import { z } from 'zod';

import { type FilterOperator } from '@apps/web/components/ui/filter_bar';
import { getDateStringInTimezone } from '@apps/web/lib/date_utils';
import { type components } from '@libraries/typescript/api_client';

type ConversationType = components['schemas']['ConversationType'];
type AgentExecutionBackend = components['schemas']['AgentExecutionBackend'];
type QueryableConversationType = Exclude<ConversationType, 'admin_playground'>;
export type AgentTraceTypeFilterValue =
    | QueryableConversationType
    | 'automation';

export const REVIEW_STATUS_VALUES = [
    'needs_review',
    'reviewed',
    'ticket_filed',
    'false_positive',
    'known_issue',
] as const;
export const REVIEW_VIEW_VALUES = [...REVIEW_STATUS_VALUES, 'all'] as const;
export const REVIEW_STATUS_LABELS: Record<
    (typeof REVIEW_STATUS_VALUES)[number],
    string
> = {
    needs_review: 'Needs review',
    reviewed: 'Reviewed',
    ticket_filed: 'Ticket filed',
    false_positive: 'False positive',
    known_issue: 'Known issue',
};
export const REVIEW_VIEW_LABELS: Record<
    (typeof REVIEW_VIEW_VALUES)[number],
    string
> = {
    needs_review: REVIEW_STATUS_LABELS.needs_review,
    reviewed: REVIEW_STATUS_LABELS.reviewed,
    ticket_filed: REVIEW_STATUS_LABELS.ticket_filed,
    false_positive: REVIEW_STATUS_LABELS.false_positive,
    known_issue: REVIEW_STATUS_LABELS.known_issue,
    all: 'All',
};
export const CONVERSATION_TYPE_VALUES = [
    'general',
    'outreach_analysis',
    'internal',
    'automation',
] as const satisfies ReadonlyArray<AgentTraceTypeFilterValue>;
export type ConversationTypeFilterValue =
    (typeof CONVERSATION_TYPE_VALUES)[number];
export function isConversationTypeFilterValue(
    value: string,
): value is ConversationTypeFilterValue {
    return (CONVERSATION_TYPE_VALUES as ReadonlyArray<string>).includes(value);
}
export const CONVERSATION_TYPE_LABELS: Record<
    AgentTraceTypeFilterValue,
    string
> = {
    general: 'General',
    outreach_analysis: 'Outreach Analysis',
    agent_trace_llm_judge: 'LLM Judge',
    internal: 'Internal',
    automation: 'Automation',
};
export const AUTO_DRAFTED_VALUES = ['yes', 'no'] as const;
export const URGENCY_FILTER_VALUES = ['urgent_outreach'] as const;
export const JUDGMENT_SELECTION_VALUES = ['newest', 'worst'] as const;
export const JUDGMENT_VERDICT_VALUES = [
    'urgent',
    'unjudgeable',
    'infrastructure_failure',
] as const;
export const EXECUTION_BACKEND_VALUES = [
    'event',
    'temporal',
    'legacy',
] as const satisfies ReadonlyArray<AgentExecutionBackend>;
export const RUN_CHANNEL_FILTER_VALUES = [
    'web',
    'imessage',
    'slack',
    'unspecified',
] as const;
export const TRACE_SOURCE_VALUES = [
    'slack',
    'linq_imessage',
    'linq_imessage_subagent',
    'admin',
    'extension',
    'training',
    'v3',
] as const;
export const REVIEW_SOURCE_VALUES = ['human'] as const;
export const HUMAN_REVIEW_STATUS_VALUES = ['reviewed', 'not_reviewed'] as const;
export const HUMAN_REVIEW_STATUS_LABELS: Record<
    (typeof HUMAN_REVIEW_STATUS_VALUES)[number],
    string
> = {
    reviewed: 'Reviewed',
    not_reviewed: 'Not Reviewed',
};
export const FEEDBACK_LABEL_VALUES = ['good', 'bad'] as const;
export const FEEDBACK_LABELS: Record<
    (typeof FEEDBACK_LABEL_VALUES)[number],
    string
> = {
    good: 'Thumbs up',
    bad: 'Thumbs down',
};
// Prompt-version filter options are no longer a static list here: they are
// fetched at runtime from GET /admin/prompt-rollout/version-filter-options
// (stamp key × discovered versions) via usePromptVersionFilterOptions, so a new
// version file or skill surfaces automatically with no hand-edited list.

export const SORT_BY_VALUES = [
    'created_at',
    'total_cost_usd',
    'duration_ms',
    'first_response_ready_latency_ms',
] as const;

/**
 * Sentinel owner-filter values. A real owner filter carries an admin user id;
 * these two stand in for the "owned by me" and "unassigned" shortcuts so the
 * whole owner dimension rides on a single `owner` search param.
 */
export const OWNER_FILTER_MINE = '@me';
export const OWNER_FILTER_UNASSIGNED = '@unassigned';

/** The owner-dimension query params the list endpoint accepts. */
export type OwnerListParams = {
    readonly owner_user_id: string | undefined;
    readonly owned_by_me: boolean | undefined;
    readonly unassigned_only: boolean | undefined;
};

/**
 * Expand the single `owner` search param into the list endpoint's three owner
 * query params: the two sentinels map to the `owned_by_me` / `unassigned_only`
 * flags, and any other value is an explicit owner user id.
 */
export function ownerSearchToListParams(
    owner: string | undefined,
): OwnerListParams {
    return {
        owner_user_id:
            owner !== undefined &&
            owner !== OWNER_FILTER_MINE &&
            owner !== OWNER_FILTER_UNASSIGNED
                ? owner
                : undefined,
        owned_by_me: owner === OWNER_FILTER_MINE ? true : undefined,
        unassigned_only: owner === OWNER_FILTER_UNASSIGNED ? true : undefined,
    };
}

const arrayParam = <T extends z.ZodTypeAny>(schema: T) =>
    z
        .preprocess((value) => {
            if (value === undefined || value === null) {
                return undefined;
            }
            if (Array.isArray(value)) {
                return value;
            }
            if (typeof value === 'string') {
                if (!value.trim()) {
                    return [];
                }
                return value
                    .split(',')
                    .map((item) => item.trim())
                    .filter(Boolean);
            }
            return value;
        }, z.array(schema))
        .optional();

const conversationTypesParam = arrayParam(z.string())
    .transform((values) => {
        const supported = values?.filter(isConversationTypeFilterValue);
        return supported?.length ? supported : undefined;
    })
    .optional();

const reviewViewParam = z
    .preprocess(
        (value) => (value === 'unreviewed' ? 'needs_review' : value),
        z.enum(REVIEW_VIEW_VALUES),
    )
    .optional()
    .catch(undefined);

/**
 * Default `created_after` bound: midnight today (local time), as a UTC
 * ISO instant. The trace list applies this when the URL has neither an
 * absolute date range nor an explicit rolling window, so a fresh visit lands
 * on the current day.
 */
export const DEFAULT_AGENT_TRACE_SORT_BY = 'created_at';
export const DEFAULT_AGENT_TRACE_SORT_ORDER = 'desc';

export function defaultCreatedAfter(referenceDate: Date = new Date()): string {
    const date = new Date(referenceDate);
    date.setHours(0, 0, 0, 0);
    return date.toISOString();
}

/** Returns the exact instant at the start of a rolling day window. */
export function rollingWindowCreatedAfter(
    windowDays: number,
    referenceDate: Date = new Date(),
): string {
    return new Date(
        referenceDate.getTime() - windowDays * 24 * 60 * 60 * 1000,
    ).toISOString();
}

export type AgentTraceDatePickerValue = {
    readonly from: string;
    readonly to: string;
};

export function agentTraceDatePickerValue(
    search: Pick<
        AgentTracesSearch,
        'created_after' | 'created_before' | 'window'
    >,
    timezone: string,
    referenceDate: Date = new Date(),
): AgentTraceDatePickerValue | undefined {
    if (
        search.created_after === undefined &&
        search.created_before === undefined
    ) {
        if (search.window === 'all') {
            return undefined;
        }
        const createdAfter =
            search.window === undefined
                ? defaultCreatedAfter(referenceDate)
                : rollingWindowCreatedAfter(search.window, referenceDate);
        return {
            from: getDateStringInTimezone(new Date(createdAfter), timezone),
            to: getDateStringInTimezone(referenceDate, timezone),
        };
    }
    return {
        from: getDateStringInTimezone(
            new Date(
                search.created_after ?? search.created_before ?? referenceDate,
            ),
            timezone,
        ),
        to: getDateStringInTimezone(
            new Date(
                search.created_before ?? search.created_after ?? referenceDate,
            ),
            timezone,
        ),
    };
}

export const agentTracesSearchSchema = z.object({
    sandbox_only: z.boolean().optional(),
    view: reviewViewParam,
    page: z.coerce.number().optional().default(1),
    organization_ids: arrayParam(z.string()),
    exclude_organization_ids: arrayParam(z.string()),
    created_by_users: arrayParam(z.string()),
    exclude_created_by_users: arrayParam(z.string()),
    // ISO instants (UTC) so shared links pin the same moment in every
    // timezone; the inputs render/edit them in the viewer's local time.
    // Absolute bounds take precedence over `window`; when all three are
    // absent, the list applies the current-day bound.
    created_after: z.string().optional(),
    created_before: z.string().optional(),
    window: z
        .union([z.literal('all'), z.coerce.number().int().min(1).max(365)])
        .optional()
        .catch(undefined),
    owners: arrayParam(z.string()),
    exclude_owners: arrayParam(z.string()),
    conversation_type: z
        .enum(CONVERSATION_TYPE_VALUES)
        .optional()
        .catch(undefined),
    conversation_types: conversationTypesParam,
    exclude_conversation_types: conversationTypesParam,
    auto_drafted: z.enum(AUTO_DRAFTED_VALUES).optional(),
    auto_drafted_values: arrayParam(z.enum(AUTO_DRAFTED_VALUES)),
    exclude_auto_drafted_values: arrayParam(z.enum(AUTO_DRAFTED_VALUES)),
    urgency: z.enum(URGENCY_FILTER_VALUES).optional(),
    judgment_selection: z.enum(JUDGMENT_SELECTION_VALUES).optional(),
    judgment_verdict: z.enum(JUDGMENT_VERDICT_VALUES).optional(),
    urgency_values: arrayParam(z.enum(URGENCY_FILTER_VALUES)),
    exclude_urgency_values: arrayParam(z.enum(URGENCY_FILTER_VALUES)),
    execution_backend: z.enum(EXECUTION_BACKEND_VALUES).optional(),
    execution_backends: arrayParam(z.enum(EXECUTION_BACKEND_VALUES)),
    exclude_execution_backends: arrayParam(z.enum(EXECUTION_BACKEND_VALUES)),
    run_channels: arrayParam(z.enum(RUN_CHANNEL_FILTER_VALUES)),
    exclude_run_channels: arrayParam(z.enum(RUN_CHANNEL_FILTER_VALUES)),
    source: z.enum(TRACE_SOURCE_VALUES).optional(),
    sources: arrayParam(z.enum(TRACE_SOURCE_VALUES)),
    exclude_sources: arrayParam(z.enum(TRACE_SOURCE_VALUES)),
    human_review_status: z.enum(HUMAN_REVIEW_STATUS_VALUES).optional(),
    human_review_statuses: arrayParam(z.enum(HUMAN_REVIEW_STATUS_VALUES)),
    exclude_human_review_statuses: arrayParam(
        z.enum(HUMAN_REVIEW_STATUS_VALUES),
    ),
    review_source: z.enum(REVIEW_SOURCE_VALUES).optional().catch(undefined),
    exclude_review_source: z
        .enum(REVIEW_SOURCE_VALUES)
        .optional()
        .catch(undefined),
    feedback_label: z.enum(FEEDBACK_LABEL_VALUES).optional(),
    feedback_labels: arrayParam(z.enum(FEEDBACK_LABEL_VALUES)),
    exclude_feedback_labels: arrayParam(z.enum(FEEDBACK_LABEL_VALUES)),
    all_feedback_labels: arrayParam(z.enum(FEEDBACK_LABEL_VALUES)),
    prompt_version: z.string().optional(),
    prompt_versions: arrayParam(z.string()),
    exclude_prompt_versions: arrayParam(z.string()),
    all_prompt_versions: arrayParam(z.string()),
    owner: z.string().optional(),
    q: z.string().optional(),
    sort_by: z.enum(SORT_BY_VALUES).optional().catch(undefined),
    sort_order: z.enum(['asc', 'desc']).optional(),
});

export type AgentTracesSearch = z.infer<typeof agentTracesSearchSchema>;
export type AgentTraceSortBy = (typeof SORT_BY_VALUES)[number];
export type SortOrder = NonNullable<AgentTracesSearch['sort_order']>;

/**
 * The detail view inherits every list filter/sort/page param so it can rebuild
 * the exact list it was opened from. `from_list` is set only when navigating
 * from the list; its absence (a shared link, bookmark, or refresh without the
 * params) is the signal that there is no list context to navigate.
 * `from_sandbox` returns to the sandbox Runs tab with the same list filters.
 */
export const agentTraceDetailSearchSchema = agentTracesSearchSchema.extend({
    admin_agent_run_id: z.string().optional(),
    admin_agent_trace_id: z.string().optional(),
    feedback_item_id: z.string().uuid().optional().catch(undefined),
    from_list: z.boolean().optional(),
    from_sandbox: z.boolean().optional(),
});

export type AgentTraceDetailSearch = z.infer<
    typeof agentTraceDetailSearchSchema
>;

export function getDetailAdminAgentRunIdForTrace(
    search: AgentTraceDetailSearch,
    conversationId: string,
): string | null {
    return search.admin_agent_trace_id === conversationId
        ? (search.admin_agent_run_id ?? null)
        : null;
}

export function detailSearchWithAdminAgentRun(
    search: AgentTraceDetailSearch,
    conversationId: string,
    runId: string | null,
): AgentTraceDetailSearch {
    return {
        ...search,
        admin_agent_run_id: runId ?? undefined,
        admin_agent_trace_id: runId ? conversationId : undefined,
    };
}

export function detailSearchForTraceNavigation(
    search: AgentTraceDetailSearch,
): AgentTraceDetailSearch {
    return {
        ...search,
        feedback_item_id: undefined,
    };
}

/** Strip detail-only state back down to list search params, for returning to
 * the list with the same filters intact. */
export function detailSearchToListSearch(
    search: AgentTraceDetailSearch,
): AgentTracesSearch {
    const {
        admin_agent_run_id: _adminAgentRunId,
        admin_agent_trace_id: _adminAgentTraceId,
        feedback_item_id: _feedbackItemId,
        from_list: _fromList,
        from_sandbox: _fromSandbox,
        ...listSearch
    } = search;
    return listSearch;
}

export type TraceFilterDimensionKey =
    | 'human_review_status'
    | 'organization'
    | 'owner'
    | 'created_by'
    | 'conversation_type'
    | 'auto_drafted'
    | 'urgency'
    | 'execution_backend'
    | 'run_channel'
    | 'source'
    | 'feedback_label'
    | 'prompt_version';

export type TraceFilterChip = {
    readonly dimension: TraceFilterDimensionKey;
    readonly operator?: FilterOperator;
    readonly values: Array<string>;
};

export const DEFAULT_AGENT_TRACE_FILTER_CHIPS: Array<TraceFilterChip> = [
    {
        dimension: 'conversation_type',
        operator: 'is_not',
        values: ['automation'],
    },
];

/** The subset of search params owned by the filter bar. */
export type AgentTraceFilterParams = {
    readonly organization_ids: Array<string> | undefined;
    readonly exclude_organization_ids: Array<string> | undefined;
    readonly created_by_users: Array<string> | undefined;
    readonly exclude_created_by_users: Array<string> | undefined;
    readonly owner: string | undefined;
    readonly owners: Array<string> | undefined;
    readonly exclude_owners: Array<string> | undefined;
    readonly conversation_type: AgentTracesSearch['conversation_type'];
    readonly conversation_types:
        | Array<NonNullable<AgentTracesSearch['conversation_type']>>
        | undefined;
    readonly exclude_conversation_types:
        | Array<NonNullable<AgentTracesSearch['conversation_type']>>
        | undefined;
    readonly auto_drafted: AgentTracesSearch['auto_drafted'];
    readonly auto_drafted_values:
        | Array<NonNullable<AgentTracesSearch['auto_drafted']>>
        | undefined;
    readonly exclude_auto_drafted_values:
        | Array<NonNullable<AgentTracesSearch['auto_drafted']>>
        | undefined;
    readonly urgency: AgentTracesSearch['urgency'];
    readonly urgency_values:
        | Array<NonNullable<AgentTracesSearch['urgency']>>
        | undefined;
    readonly exclude_urgency_values:
        | Array<NonNullable<AgentTracesSearch['urgency']>>
        | undefined;
    readonly execution_backend: AgentTracesSearch['execution_backend'];
    readonly execution_backends:
        | Array<NonNullable<AgentTracesSearch['execution_backend']>>
        | undefined;
    readonly exclude_execution_backends:
        | Array<NonNullable<AgentTracesSearch['execution_backend']>>
        | undefined;
    readonly run_channels: AgentTracesSearch['run_channels'];
    readonly exclude_run_channels: AgentTracesSearch['exclude_run_channels'];
    readonly source: AgentTracesSearch['source'];
    readonly sources:
        | Array<NonNullable<AgentTracesSearch['source']>>
        | undefined;
    readonly exclude_sources:
        | Array<NonNullable<AgentTracesSearch['source']>>
        | undefined;
    readonly human_review_status: AgentTracesSearch['human_review_status'];
    readonly human_review_statuses:
        | Array<NonNullable<AgentTracesSearch['human_review_status']>>
        | undefined;
    readonly exclude_human_review_statuses:
        | Array<NonNullable<AgentTracesSearch['human_review_status']>>
        | undefined;
    readonly review_source: AgentTracesSearch['review_source'];
    readonly exclude_review_source: AgentTracesSearch['exclude_review_source'];
    readonly feedback_label: AgentTracesSearch['feedback_label'];
    readonly feedback_labels:
        | Array<NonNullable<AgentTracesSearch['feedback_label']>>
        | undefined;
    readonly exclude_feedback_labels:
        | Array<NonNullable<AgentTracesSearch['feedback_label']>>
        | undefined;
    readonly all_feedback_labels:
        | Array<NonNullable<AgentTracesSearch['feedback_label']>>
        | undefined;
    readonly prompt_version: string | undefined;
    readonly prompt_versions: Array<string> | undefined;
    readonly exclude_prompt_versions: Array<string> | undefined;
    readonly all_prompt_versions: Array<string> | undefined;
};

function legacyReviewSourceToStatus(
    search: AgentTracesSearch,
): AgentTracesSearch['human_review_status'] {
    if (search.review_source === 'human') {
        return 'reviewed';
    }
    if (search.exclude_review_source === 'human') {
        return 'not_reviewed';
    }
    return undefined;
}

function hasFilterValues<T extends string>(values: Array<T> | undefined) {
    return (values ?? []).some((value) => value.trim().length > 0);
}

function hasAnyFilterValues<T extends string>(
    includeValues: Array<T> | undefined,
    excludeValues: Array<T> | undefined,
) {
    return hasFilterValues(includeValues) || hasFilterValues(excludeValues);
}

function includeValuesWithLegacy<T extends string>(
    includeValues: Array<T> | undefined,
    legacyValue: T | undefined,
    hasPluralFilter: boolean,
): Array<T> | undefined {
    if (hasPluralFilter) {
        return hasFilterValues(includeValues) ? includeValues : undefined;
    }
    return legacyValue ? [legacyValue] : undefined;
}

/**
 * Maps URL search params to filter-bar chips. The order here is the stable
 * render order of chips in the bar.
 */
export function searchToFilterChips(
    search: AgentTracesSearch,
): Array<TraceFilterChip> {
    const chips: Array<TraceFilterChip> = [];
    const pushChip = (
        dimension: TraceFilterDimensionKey,
        values: Array<string> | undefined,
        operator: FilterOperator = 'is',
    ) => {
        const filteredValues = (values ?? [])
            .map((value) => value.trim())
            .filter(Boolean);
        if (filteredValues.length > 0) {
            chips.push({ dimension, operator, values: filteredValues });
        }
    };
    const hasHumanReviewPluralFilter = hasAnyFilterValues(
        search.human_review_statuses,
        search.exclude_human_review_statuses,
    );
    const humanReviewStatus =
        search.human_review_status !== undefined || hasHumanReviewPluralFilter
            ? search.human_review_status
            : legacyReviewSourceToStatus(search);
    const hasOwnerFilter = hasAnyFilterValues(
        search.owners,
        search.exclude_owners,
    );
    const hasConversationTypeFilter = hasAnyFilterValues(
        search.conversation_types,
        search.exclude_conversation_types,
    );
    const hasAutoDraftedFilter = hasAnyFilterValues(
        search.auto_drafted_values,
        search.exclude_auto_drafted_values,
    );
    const hasUrgencyFilter = hasAnyFilterValues(
        search.urgency_values,
        search.exclude_urgency_values,
    );
    const hasExecutionBackendFilter = hasAnyFilterValues(
        search.execution_backends,
        search.exclude_execution_backends,
    );
    const hasSourceFilter = hasAnyFilterValues(
        search.sources,
        search.exclude_sources,
    );
    const hasFeedbackFilter = hasAnyFilterValues(
        search.feedback_labels,
        search.exclude_feedback_labels,
    );
    const hasPromptVersionFilter = hasAnyFilterValues(
        search.prompt_versions,
        search.exclude_prompt_versions,
    );
    pushChip(
        'human_review_status',
        includeValuesWithLegacy(
            search.human_review_statuses,
            humanReviewStatus,
            hasHumanReviewPluralFilter,
        ),
    );
    pushChip(
        'human_review_status',
        search.exclude_human_review_statuses,
        'is_not',
    );
    pushChip('organization', search.organization_ids);
    pushChip('organization', search.exclude_organization_ids, 'is_not');
    pushChip('created_by', search.created_by_users);
    pushChip('created_by', search.exclude_created_by_users, 'is_not');
    pushChip(
        'owner',
        includeValuesWithLegacy(search.owners, search.owner, hasOwnerFilter),
    );
    pushChip('owner', search.exclude_owners, 'is_not');
    const includedConversationTypes = includeValuesWithLegacy(
        search.conversation_types,
        search.conversation_type,
        hasConversationTypeFilter,
    );
    pushChip('conversation_type', includedConversationTypes);
    pushChip('conversation_type', search.exclude_conversation_types, 'is_not');
    pushChip(
        'auto_drafted',
        includeValuesWithLegacy(
            search.auto_drafted_values,
            search.auto_drafted,
            hasAutoDraftedFilter,
        ),
    );
    pushChip('auto_drafted', search.exclude_auto_drafted_values, 'is_not');
    pushChip(
        'urgency',
        includeValuesWithLegacy(
            search.urgency_values,
            search.urgency,
            hasUrgencyFilter,
        ),
    );
    pushChip('urgency', search.exclude_urgency_values, 'is_not');
    pushChip(
        'execution_backend',
        includeValuesWithLegacy(
            search.execution_backends,
            search.execution_backend,
            hasExecutionBackendFilter,
        ),
    );
    pushChip('execution_backend', search.exclude_execution_backends, 'is_not');
    pushChip('run_channel', search.run_channels);
    pushChip('run_channel', search.exclude_run_channels, 'is_not');
    pushChip(
        'source',
        includeValuesWithLegacy(search.sources, search.source, hasSourceFilter),
    );
    pushChip('source', search.exclude_sources, 'is_not');
    pushChip(
        'feedback_label',
        includeValuesWithLegacy(
            search.feedback_labels,
            search.feedback_label,
            hasFeedbackFilter,
        ),
    );
    pushChip('feedback_label', search.all_feedback_labels, 'is_all');
    pushChip('feedback_label', search.exclude_feedback_labels, 'is_not');
    pushChip(
        'prompt_version',
        includeValuesWithLegacy(
            search.prompt_versions,
            search.prompt_version,
            hasPromptVersionFilter,
        ),
    );
    pushChip('prompt_version', search.all_prompt_versions, 'is_all');
    pushChip('prompt_version', search.exclude_prompt_versions, 'is_not');
    return chips;
}

/** The filter params whose values are lists, which every chip writes to. */
type ChipArrayParam = {
    [K in keyof AgentTraceFilterParams]-?: NonNullable<
        AgentTraceFilterParams[K]
    > extends Array<string>
        ? K
        : never;
}[keyof AgentTraceFilterParams];

type ChipParamSlots = {
    // null accepts any non-blank value, such as ids.
    readonly allowedValues: ReadonlyArray<string> | null;
    readonly is: ChipArrayParam;
    readonly is_not: ChipArrayParam;
    // Dimensions without an "is all" param treat that operator as "is".
    readonly is_all?: ChipArrayParam;
};

/**
 * The param each chip operator writes, per dimension. A later chip for the
 * same slot replaces an earlier one. Conversation type chips are the
 * exception and are handled in filterChipsToSearchParams.
 */
const CHIP_PARAM_SLOTS: Record<
    Exclude<TraceFilterDimensionKey, 'conversation_type'>,
    ChipParamSlots
> = {
    human_review_status: {
        allowedValues: HUMAN_REVIEW_STATUS_VALUES,
        is: 'human_review_statuses',
        is_not: 'exclude_human_review_statuses',
    },
    organization: {
        allowedValues: null,
        is: 'organization_ids',
        is_not: 'exclude_organization_ids',
    },
    owner: {
        allowedValues: null,
        is: 'owners',
        is_not: 'exclude_owners',
    },
    created_by: {
        allowedValues: null,
        is: 'created_by_users',
        is_not: 'exclude_created_by_users',
    },
    auto_drafted: {
        allowedValues: AUTO_DRAFTED_VALUES,
        is: 'auto_drafted_values',
        is_not: 'exclude_auto_drafted_values',
    },
    urgency: {
        allowedValues: URGENCY_FILTER_VALUES,
        is: 'urgency_values',
        is_not: 'exclude_urgency_values',
    },
    execution_backend: {
        allowedValues: EXECUTION_BACKEND_VALUES,
        is: 'execution_backends',
        is_not: 'exclude_execution_backends',
    },
    run_channel: {
        allowedValues: RUN_CHANNEL_FILTER_VALUES,
        is: 'run_channels',
        is_not: 'exclude_run_channels',
    },
    source: {
        allowedValues: TRACE_SOURCE_VALUES,
        is: 'sources',
        is_not: 'exclude_sources',
    },
    feedback_label: {
        allowedValues: FEEDBACK_LABEL_VALUES,
        is: 'feedback_labels',
        is_not: 'exclude_feedback_labels',
        is_all: 'all_feedback_labels',
    },
    prompt_version: {
        allowedValues: null,
        is: 'prompt_versions',
        is_not: 'exclude_prompt_versions',
        is_all: 'all_prompt_versions',
    },
};

/** Trimmed, non-blank chip values in the allowed set, or undefined if none. */
function allowedChipValues(
    values: Array<string>,
    allowedValues: ReadonlyArray<string> | null,
): Array<string> | undefined {
    const filteredValues = values.map((value) => value.trim()).filter(Boolean);
    if (allowedValues === null) {
        return filteredValues.length > 0 ? filteredValues : undefined;
    }
    const allowed = new Set<string>(allowedValues);
    const validValues = filteredValues.filter((value) => allowed.has(value));
    return validValues.length > 0 ? validValues : undefined;
}

/**
 * Inverse of searchToFilterChips. Returns every filter-owned param so that
 * dimensions absent from the chips are explicitly cleared from the URL.
 */
export function filterChipsToSearchParams(
    chips: Array<TraceFilterChip>,
): AgentTraceFilterParams {
    const params: {
        -readonly [
            K in keyof AgentTraceFilterParams
        ]: AgentTraceFilterParams[K];
    } = {
        organization_ids: undefined,
        exclude_organization_ids: undefined,
        created_by_users: undefined,
        exclude_created_by_users: undefined,
        owner: undefined,
        owners: undefined,
        exclude_owners: undefined,
        conversation_type: undefined,
        conversation_types: undefined,
        exclude_conversation_types: undefined,
        auto_drafted: undefined,
        auto_drafted_values: undefined,
        exclude_auto_drafted_values: undefined,
        urgency: undefined,
        urgency_values: undefined,
        exclude_urgency_values: undefined,
        execution_backend: undefined,
        execution_backends: undefined,
        exclude_execution_backends: undefined,
        run_channels: undefined,
        exclude_run_channels: undefined,
        source: undefined,
        sources: undefined,
        exclude_sources: undefined,
        human_review_status: undefined,
        human_review_statuses: undefined,
        exclude_human_review_statuses: undefined,
        review_source: undefined,
        exclude_review_source: undefined,
        feedback_label: undefined,
        feedback_labels: undefined,
        exclude_feedback_labels: undefined,
        all_feedback_labels: undefined,
        prompt_version: undefined,
        prompt_versions: undefined,
        exclude_prompt_versions: undefined,
        all_prompt_versions: undefined,
    };
    for (const chip of chips) {
        if (chip.values.length === 0) {
            continue;
        }
        const operator = chip.operator ?? 'is';
        if (chip.dimension === 'conversation_type') {
            // Conversation type chips add to each other instead of replacing.
            const assignedValues = allowedChipValues(
                chip.values,
                CONVERSATION_TYPE_VALUES,
            ) as AgentTraceFilterParams['conversation_types'];
            if (assignedValues === undefined) {
                continue;
            }
            if (operator === 'is_not') {
                params.exclude_conversation_types = [
                    ...(params.exclude_conversation_types ?? []),
                    ...assignedValues,
                ];
            } else {
                params.conversation_types = [
                    ...(params.conversation_types ?? []),
                    ...assignedValues,
                ];
            }
            continue;
        }
        // An off-type dimension fails with a clear error, not a lookup crash.
        if (
            !Object.prototype.hasOwnProperty.call(
                CHIP_PARAM_SLOTS,
                chip.dimension,
            )
        ) {
            throw new Error(`Unhandled filter dimension: ${chip.dimension}`);
        }
        const slots = CHIP_PARAM_SLOTS[chip.dimension];
        const param = slots[operator] ?? slots.is;
        // Values are checked against the slot's allowed values above, which
        // is what narrows them to the param's own value type.
        (params as Record<ChipArrayParam, Array<string> | undefined>)[param] =
            allowedChipValues(chip.values, slots.allowedValues);
    }
    return params;
}
